import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { api, type Project } from "../../api";
import { Diagram, Legend, type ArchGraph } from "../../components/diagram";
import { relative } from "../../components/ui";

// The question: what does this environment really consist of, from the name a user types to the
// database it writes, and what talks to what, in AWS and on the lab host? Lab environments: the
// release the lab runs (compose + Caddy site file) placed on the lab host with its Route 53 names,
// RDS, SSM and AWS services. Others: what AWS says, read-only.

interface EnvArchitecture {
  env: string;
  onLab: boolean;
  available: boolean;
  reason?: string;
  release?: { sha: string | null; compose: string; composeFrom: "catalog" | "running containers" | "release default"; site: string | null; at: string | null };
  resources?: { checkedAt: string; errors: string[] };
  graph?: ArchGraph;
}

const FROM: Record<NonNullable<EnvArchitecture["release"]>["composeFrom"], string> = {
  catalog: "",
  "running containers": " (from the running containers; the catalog names none)",
  "release default": " (the release's default; the catalog names none)",
};

export function ArchitectureTab({ project }: { project: Project }) {
  const q = useQuery({ queryKey: ["architecture", project.slug], queryFn: () => api<{ environments: EnvArchitecture[] }>(`/projects/${project.slug}/architecture`) });
  const [params, setParams] = useSearchParams();
  const env = params.get("env") ?? project.environments[0]?.name ?? "";
  if (q.isPending) return <div className="panel"><span className="skeleton" style={{ width: "100%", height: 280 }} /><p className="small muted" style={{ marginTop: 8 }}>Reading the release and AWS…</p></div>;
  if (q.error) return <div className="panel error">{q.error.message}</div>;
  const envs = q.data.environments;
  if (!envs.length) return <div className="empty">No environment yet.</div>;
  const a = envs.find((e) => e.env === env) ?? envs[0]!;
  const pick = (name: string) => setParams((p) => { const n = new URLSearchParams(p); n.set("tab", "architecture"); n.set("env", name); return n; });

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="toolbar">
        {envs.length > 1 ? (
          <select className="input" style={{ height: 32, width: 240 }} aria-label="Environment" value={a.env} onChange={(e) => pick(e.target.value)}>
            {envs.map((e) => <option key={e.env} value={e.env}>{e.env} · {e.onLab ? "on the lab" : "outside the lab"}</option>)}
          </select>
        ) : <span className="mono">{a.env}</span>}
        {a.release && (
          <span className="stamp">
            Release <span className="mono">{a.release.sha?.slice(0, 8) ?? "?"}</span>{a.release.at ? `, unpacked ${relative(a.release.at)}` : ""} · <span className="mono">{a.release.compose}</span>{FROM[a.release.composeFrom]}
            {a.release.site ? <> · <span className="mono">{a.release.site.split("/").pop()}</span></> : " · no site file"}
          </span>
        )}
        {a.resources && <span className="stamp">Read from AWS {relative(a.resources.checkedAt)} (kept 10 min)</span>}
      </div>

      {!a.available && <div className="empty">{a.reason}</div>}
      {a.graph && (
        <>
          {a.graph.warnings.length > 0 && (
            <section className="panel flush" aria-label="Warnings">
              <div className="list">
                {a.graph.warnings.map((w) => (
                  <div className="item" key={w} style={{ gridTemplateColumns: "auto minmax(0, 1fr)" }}>
                    <span className="pill negative">Warning</span><span>{w}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
          <section className="panel flush" aria-label={`Architecture of ${a.env}`}>
            <Diagram g={a.graph} label={`System design of ${project.name} ${a.env}`} />
            <div style={{ padding: "4px 20px 16px" }}><Legend /></div>
          </section>
          <div className="split">
            <section className="panel" aria-labelledby="paths">
              <h2 id="paths" style={{ marginBottom: 12 }}>Request path</h2>
              {a.graph.requestPaths.length === 0 ? <p className="muted">Nothing public.</p> : (
                <ol className="paths">{a.graph.requestPaths.map((p) => <li key={p} className="mono">{p}</li>)}</ol>
              )}
            </section>
            <aside className="panel" aria-labelledby="arch-notes">
              <h2 id="arch-notes" style={{ marginBottom: 12 }}>How this was drawn</h2>
              <p className="muted" style={{ marginBottom: 8 }}>
                {a.onLab
                  ? "From the compose file of the release the lab runs and the Caddy site file it installed, placed on the lab host with its Route 53 records, the shared RDS, and the SSM settings it is started with. Calls come from service settings that name a sibling host; outside services from setting names. Only names are read, never values."
                  : "From AWS, read-only: the instances and clusters this environment watches, what is tagged with its project, and the DNS names, load balancers and CloudFront distributions that lead to them. Links marked likely (a database in the same VPC) are not in any configuration AWS holds."}
              </p>
              {a.graph.notes.map((n) => <p key={n} className="small muted" style={{ marginTop: 6 }}>{n}</p>)}
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
